import { type Subproject as CardSubproject } from '@/organization/landing/components/projectCard/types';
import { getOverallStatus } from '@/organization/landing/components/projectCard/utils';
import { getSubprojectStatusIcon } from '@/organization/landing/statusUtils';
import { type Subproject } from '@/stores/SubprojectStore';
import { Box, Tooltip } from '@mui/material';

/** Cast SubprojectStore.Subproject to projectCard Subproject for getOverallStatus */
function toCardSubproject(s: Subproject): CardSubproject {
  return s as unknown as CardSubproject;
}

interface SubprojectProgressGridProps {
  subprojects: Subproject[];
  onSubprojectChange: (subprojectId: string) => void;
}

/**
 * Renders a grid of subproject progress pill icons when there are 10 or fewer subprojects.
 * Each pill is clickable (navigates via onSubprojectChange) and shows a tooltip with the
 * subproject name on hover. The array is reversed so finished items accumulate from bottom-right.
 */
export function SubprojectProgressGrid({
  subprojects,
  onSubprojectChange,
}: SubprojectProgressGridProps) {
  // Reverse so last subproject (index N) occupies top-left cell and first (index 0) occupies
  // bottom-right cell. Finished items (typically later in the list) accumulate from bottom-right.
  const reversed = [...subprojects].reverse();

  return (
    <Box
      sx={{
        display: 'grid',
        gap: '6px',
        gridTemplateRows: 'auto auto',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
      }}
    >
      {reversed.map((sub) => (
        <Tooltip key={sub.id} title={sub.name} arrow placement='top'>
          <Box
            onClick={() => onSubprojectChange(sub.id)}
            sx={{ cursor: 'pointer', display: 'inline-block' }}
          >
            <Box
              component='img'
              src={getSubprojectStatusIcon(
                getOverallStatus(toCardSubproject(sub)),
              )}
              alt={`${sub.name} - ${getOverallStatus(toCardSubproject(sub))}`}
              sx={{
                width: 'clamp(44px, 3.5vw, 68px)',
                height: 'clamp(12px, 0.9vw, 18px)',
                display: 'block',
                imageRendering: 'crisp-edges',
              }}
            />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}
